import {
  ConsoleApiName,
  LoggerConfig,
  ILogger,
  ConsoleLogger,
  NewRelicLogger,
  LoggerService,
  LogLevel,
  LoggerType,
  WriteToLog,
} from '@trimble/logging';
import { createContext, JSX, onCleanup, useContext } from 'solid-js';
import { UnreachableCaseError } from 'ts-essentials';
import { parseEnumValue } from '@trimble/utilities';

export interface LoggerContextType {
  getLogger(
    /**
     * Optional name for the logger. Messages will be prefixed with this name.
     * Typically the name of the component, service or module that is using the logger.
     *
     * If omitted the logger type will be used as the name. For example, 'console' or 'newrelic'.
     */
    name?: string
  ): ILogger;
  setProjectContext(project?: {
    id: string;
    name: string;
    location: string;
  }): void;
}

const LoggerContext = createContext<LoggerContextType>();

// Fallback when no provider exists
const getFallbackLogger = (() => {
  let instance: ConsoleLogger | undefined;
  return (): ConsoleLogger => {
    if (!instance) {
      const urlParams = new URLSearchParams(window.location.search);
      const logLevel = getConfiguredLogLevel(
        'console',
        urlParams,
        LogLevel.Debug
      );
      instance = new ConsoleLogger('console', logLevel);
    }
    return instance;
  };
})();

const fallbackContext: LoggerContextType = {
  getLogger: (name?: string): ILogger => {
    const service = LoggerService.getOrCreateInstance([getFallbackLogger()]);
    return name ? service.createNamedLogger(name) : service;
  },
  setProjectContext: (project) => {
    LoggerService.getOrCreateInstance([getFallbackLogger()]).setProjectContext(
      project
    );
  },
};

export function LoggerProvider(props: {
  captureConsoleLogs?: ConsoleApiName[] | 'all';
  loggerConfigs: LoggerConfig[];
  children: JSX.Element;
}): JSX.Element {
  const urlParams = new URLSearchParams(window.location.search);

  // eslint-disable-next-line solid/reactivity
  const loggers = props.loggerConfigs.map((loggerConfig) => {
    const loggerType = loggerConfig.type;
    const name = loggerType.toString();

    // check for logLevel override via query parameter
    const logLevel = getConfiguredLogLevel(
      loggerType,
      urlParams,
      loggerConfig.logLevel
    );

    switch (loggerType) {
      case 'console':
        return new ConsoleLogger(name, logLevel);

      case 'newrelic':
        return new NewRelicLogger(name, logLevel, loggerConfig.config);

      default:
        throw new UnreachableCaseError(loggerType);
    }
  });

  // Reset the logger service instance, in case the service was
  // already initialized by fallbackContext.
  const loggerService = LoggerService.resetInstance(loggers);
  const namedMap = new Map<string, ILogger>();

  // eslint-disable-next-line solid/reactivity
  const captureConsoleLogs = props.captureConsoleLogs;
  let cleanupConsoleLogs: (() => void) | undefined;
  if (captureConsoleLogs !== undefined && captureConsoleLogs.length > 0) {
    cleanupConsoleLogs = setupCaptureConsoleLogs(
      loggerService,
      captureConsoleLogs
    );
  }

  const getLogger = (name?: string): ILogger => {
    if (!name) return loggerService;
    if (namedMap.has(name)) return namedMap.get(name)!;

    const instance = loggerService.createNamedLogger(name);
    namedMap.set(name, instance);
    return instance;
  };

  const setProjectContext = (project?: {
    id: string;
    name: string;
    location: string;
  }): void => {
    loggerService.setProjectContext(project);
  };

  onCleanup(() => {
    cleanupConsoleLogs?.();
  });

  return (
    <LoggerContext.Provider value={{ getLogger, setProjectContext }}>
      {props.children}
    </LoggerContext.Provider>
  );
}

export function useLogger(): LoggerContextType {
  const ctx = useContext(LoggerContext);
  return ctx ?? fallbackContext;
}

function setupCaptureConsoleLogs(
  loggerService: LoggerService,
  captureConsoleLogs: ReadonlyArray<ConsoleApiName> | 'all'
): () => void {
  const originalConsole = window.console;

  const handlersConfig: Record<ConsoleApiName, WriteToLog> = {
    info: loggerService.info.bind(loggerService),
    warn: loggerService.warn.bind(loggerService),
    error: loggerService.error.bind(loggerService),
    debug: loggerService.debug.bind(loggerService),
    log: loggerService.debug.bind(loggerService),
  };

  const keysToCapture = new Set<string>(
    captureConsoleLogs === 'all'
      ? Object.keys(handlersConfig)
      : captureConsoleLogs.filter((k) => k in handlersConfig)
  );

  window.console = new Proxy(window.console, {
    get(target, prop: string | symbol) {
      if (typeof prop === 'string' && keysToCapture.has(prop)) {
        return (...args: unknown[]) => {
          const message =
            typeof args[0] === 'string' ? args[0] : String(args[0] ?? '');

          const context = args.length > 1 ? { args: args.slice(1) } : undefined;

          handlersConfig[prop as ConsoleApiName](message, context);
        };
      }

      return Reflect.get(target, prop);
    },
  });

  // Return cleanup function
  return () => {
    window.console = originalConsole;
  };
}

function getConfiguredLogLevel(
  loggerType: LoggerType,
  urlParams: URLSearchParams,
  defaultLogLevel: LogLevel
): LogLevel {
  const logLevelOverride = urlParams.get(`${loggerType}_log`);
  const logLevelOverrideEnum = logLevelOverride
    ? parseEnumValue(LogLevel, logLevelOverride)
    : undefined;
  return logLevelOverrideEnum ?? defaultLogLevel;
}
